




















































































































































































































































import Vue from 'vue';

// Assets
import Logo from '~/assets/img/logo.svg';

// Services & types
import CandidateService, { Candidate, WorkAuthorization } from '~/services/CandidateService';
import { User } from 'talkjs/all';
import { CandidateStudyProgram } from '~/services/types/csp';
import { ImportSection, ProfileImportResult, Section, SectionType } from '~/services/types/import';
import {
    anyItemNeedAttention,
    anySectionNeedAttention,
    getImportErrorDescription,
    getImportTypeDescription,
} from '~/utils/candidate/ProfileImportHelper';
import DialogService from '~/services/DialogService';
import { format } from '~/utils/date-fns';
import { parseISO } from 'date-fns';

// Components
import Button from '~/components/builders/Base/Button.vue';
import Modal from '~/components/builders/Base/Modal.vue';
import Input from '~/components/builders/Base/Input.vue';
import Checkbox from '~/components/builders/Base/Checkbox.vue';
import LanguagesModal from '~/components/profile/modals/LanguagesModal.vue';
import StudyProgramsModal from '../modals/StudyProgramsModal.vue';
import PersonalDetailsModal from '../modals/PersonalDetailsModal.vue';
import MembershipModal from '../modals/MembershipModal.vue';
import WorkExperienceModal from '../modals/WorkExperienceModal.vue';
import ImportBox from '~/components/profile/components/ImportBox.vue';
import SummaryModal from '~/components/profile/modals/SummaryModal.vue';

export enum ImportModes {
    EnterId = 'EnterId',
    SelectWhatToImport = 'SelectWhatToImport',
    InProgress = 'InProgress',
    Results = 'Results',
}

type SectionItem = {
    id: SectionType;
    selected: boolean;
    records: [];
    isAction: false;
};

export default Vue.extend({
    name: 'ProfileImportModal',
    components: {
        Modal,
        Input,
        Button,
        Checkbox,
        LanguagesModal,
        StudyProgramsModal,
        PersonalDetailsModal,
        MembershipModal,
        WorkExperienceModal,
        ImportBox,
        SummaryModal,
    },
    data() {
        return {
            candidateService: new CandidateService(),

            Logo,

            state: ImportModes.EnterId,

            link: '',
            show: false,

            importFailedError: null as string | null,

            isExpanded: false,

            showBasicModal: false,
            showLanguagesModal: false,
            showEducationModal: false,
            showWorkExperienceModal: false,
            showMembershipModal: false,
            showSummaryModal: false,

            Sections: [
                { id: SectionType.Overview, selected: true },
                { id: SectionType.Summary, selected: true },
                { id: SectionType.Languages, selected: true },
                { id: SectionType.Education, selected: true },
                { id: SectionType.WorkExperience, selected: true },
                { id: SectionType.Memberships, selected: true },
            ] as SectionItem[],
        };
    },
    computed: {
        resultViewOnlyMode(): boolean {
            return this.state === ImportModes.Results && !!this.lastProfileImport?.reviewedAt;
        },
        importedAt(): string | null {
            if (this.lastProfileImport === null) return null;
            return format(parseISO(this.lastProfileImport.createdAt.toString()), 'dd.MM.yyyy');
        },
        lastProfileImport(): ProfileImportResult | null {
            return this.$store.getters['profileModule/getLastProfileImport']();
        },
        user(): User | null {
            return this.$store.getters['profileModule/getUser']();
        },
        candidate(): Candidate | null {
            return this.$store.getters['profileModule/getCandidate']();
        },
        workAuthorizations(): WorkAuthorization[] | null {
            return this.$store.getters['profileModule/getWorkAuthorizations']();
        },
        studyPrograms(): CandidateStudyProgram[] {
            return this.$store.getters['profileModule/getSortedStudyPrograms']();
        },
        canGoNext(): boolean {
            if (this.state === ImportModes.EnterId) return this.linkValid;

            if (this.state === ImportModes.SelectWhatToImport)
                return this.linkValid && this.importSectionsSelectionValid;

            return true;
        },
        linkValid(): boolean {
            return new RegExp('https?://(?:www.)?linkedin.com/in/.+').test(this.link.trim());
        },
        importSectionsSelectionValid(): boolean {
            return this.Sections.some(x => x.selected);
        },
        anySectionNeedAttention(): boolean {
            return (
                this.lastProfileImport != null &&
                anySectionNeedAttention(this.lastProfileImport.result.sections)
            );
        },
    },
    methods: {
        onSectionSelected(item: SectionItem) {
            item.selected = !item.selected;
        },
        sectionNeedAttention(value: Section): boolean {
            return anyItemNeedAttention(value.items);
        },
        sectionNameByType(type: string): string {
            return getImportTypeDescription(type);
        },
        getImportSection(sectionType: string): ImportSection | undefined {
            if (!this.lastProfileImport) return undefined;

            const section = this.lastProfileImport.result.sections.find(
                x => x.sectionType === sectionType
            );
            if (!section) return undefined;

            return {
                id: this.lastProfileImport.id,
                createdAt: this.lastProfileImport.createdAt,
                section: section,
            };
        },
        handleShortCutSection(sectionType: string) {
            switch (sectionType) {
                case SectionType.Overview:
                    this.showBasicModal = true;
                    break;
                case SectionType.Languages:
                    this.showLanguagesModal = true;
                    break;
                case SectionType.Education:
                    this.showEducationModal = true;
                    break;
                case SectionType.WorkExperience:
                    this.showWorkExperienceModal = true;
                    break;
                case SectionType.Memberships:
                    this.showMembershipModal = true;
                    break;
                case SectionType.Summary:
                    this.showSummaryModal = true;
                    break;
            }
        },
        finishReview() {
            if (!this.lastProfileImport) return;

            this.candidateService
                .updateLastProfileImportReviewed(this.lastProfileImport.id, true)
                .finally(() => window.dispatchEvent(new CustomEvent('candidate:profile:update')));
        },
        handleNext() {
            this.importFailedError = null;

            if (this.state === ImportModes.EnterId) {
                this.state = ImportModes.SelectWhatToImport;
            } else if (this.state === ImportModes.SelectWhatToImport) {
                this.state = ImportModes.InProgress;

                this.candidateService
                    .importProfile(
                        this.link,
                        this.Sections.filter(x => x.selected).map(({ id }) => id) as SectionType[]
                    )
                    .then(importResult => {
                        if (importResult) {
                            window.dispatchEvent(new CustomEvent('candidate:profile:update'));
                            this.state = ImportModes.Results;
                        } else {
                            this.importFailedError = getImportErrorDescription(importResult);
                            this.state = ImportModes.SelectWhatToImport;
                        }
                    })
                    .catch(error => {
                        this.importFailedError = getImportErrorDescription(error);
                        this.state = ImportModes.SelectWhatToImport;
                    });
            } else if (this.state === ImportModes.Results) {
                if (this.anySectionNeedAttention) {
                    this.askFinishImportReview();
                } else {
                    this.finishReview();
                    this.handleClose();
                }
            }
        },
        handleClose() {
            this.$emit('close');
            this.show = false;
        },
        handleShow(p: any) {
            this.state = p.detail;
            this.importFailedError = null;
            this.Sections.forEach(x => (x.selected = true));
            this.link = '';
            this.show = true;
        },
        askFinishImportReview() {
            const executeAndCloseAction = ({ res }: { res: (s: string) => void }) => {
                res('close');
                this.finishReview();
                this.handleClose();
            };
            const closeAction = ({ res }: { res: (s: string) => void }) => {
                res('close');
            };

            DialogService.openDialog({
                title: String(this.$t('profile.import.finishReviewConfirm.title')),
                contentHTML: String(this.$t('profile.import.finishReviewConfirm.text')),
                actions: [
                    {
                        title: String(this.$t('general.finish')),
                        variant: 'outline-primary',
                        action: executeAndCloseAction,
                    },
                    {
                        title: String(this.$t('general.cancel')),
                        variant: 'outline-secondary',
                        action: closeAction,
                    },
                ],
            });
        },
    },
    watch: {
        linkValid(value: boolean) {
            if (value && this.state === ImportModes.EnterId)
                this.state = ImportModes.SelectWhatToImport;
        },
    },
    mounted() {
        window.addEventListener('profileimport:modal:show', this.handleShow);

        this.$root.$on('bv::collapse::state', (id: any, collapsed: any) => {
            const el = document.getElementById(id + '_chevron');
            if (el) {
                if (collapsed) el.classList.remove('rotated');
                else el.classList.add('rotated');
            }
        });
    },
    beforeDestroy() {
        window.removeEventListener('profileimport:modal:show', this.handleShow);
        this.$root.$off('bv::collapse::state');
    },
});
